import Vue from 'vue';

/**
 * INFO: this is a mixin that should be used in all Row (group, row, tax, total) components.
 * It has all the needed common behavior: display all rows in an Order, give them logic and
 * update when needed.
 *
 * HOWTO: To use it in the component, follow:
 * 1) mixins: [RowMixin],
 *
 * 2) <MyRow :order=order :row=row row-type="someRowType" />
 *
 * 3) data: {COMPONENTS: {}} – provide an object with list of the component types used in the row.
 * Usually, the types (or the keys of COMPONENTS object) are all variations of types from the
 * {@link fields_main} or {@link fields_act}. See column[someRowType] (yes, this is why you need the
 * someRowType – it distinguishes how specific column will work in the row.
 *
 * 4) Quite the same mark-up for all rows:
 *
   <template>
     <div class="grid-string grid-string-data">
       <template v-for="field in fields">
         <component
           :is="field.component"
           :key="(field.pre_slug ? field.pre_slug + field.slug : field.slug) + field.upd"
           v-bind="field.props"
           v-on="field.events"
         />
       </template>
     </div>
   </template>
 *
 * NOTICE: field.pre_slug is used in the {@link fields_main} and means that the data should
 * be addressed not as this.data[field.slug], but as this.data[field.pre_slug][field.slug],
 * because the act_row back-end view contains the usual row inside of it.
 *
 * NOTICE2: You can also give the <component> specific props, if you want, or leave as is.
 *
 * NOTICE3: If you have some problems with the updates of the component,
 * just do this.getFields() any time.
 */

export default Vue.extend({
  props: {
    order: {
      type: Array,
      default: () => [],
      required: true,
    },

    row: {
      type: Object,
      default: () => {},
    },

    rowType: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      data: this.row,
      errors: {},
      rawFields: [],
    };
  },

  computed: {
    fields() {
      return this.rawFields;
    },
  },

  watch: {
    row(val) {
      this.data = val;
    },
    data() {
      this.getFields();
    },
    order() {
      this.getFields();
    },
    errors() {
      this.getFields();
    },
  },

  created() {
    this.getFields();
  },

  methods: {
    /** @name row_fields
     * Get filtered fields with prepared props and events for dynamic component
     */
    getFields() {
      if (typeof this.COMPONENTS === 'undefined')
        throw new Error('Add data.COMPONENTS with list of components for the row cells');

      const allFields = this.order.map((group) => group.fields.map((field) => field)).flat();
      this.rawFields = allFields
        .filter((field) => !!this.COMPONENTS[field[this.rowType].type])
        .map((field) => ({
          ...field,
          upd: Date.now(),
          component: this.COMPONENTS[field[this.rowType].type].component,
          props: {
            ...field[this.rowType],
            ...this.COMPONENTS[field[this.rowType].type].props(field),
          },
          events: this.COMPONENTS[field[this.rowType].type].events(field),
        }));
    },
  },
});
